export type ProjectStatus = 'PLANNING' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
export type ProjectPhase =
  | 'PURSUIT'
  | 'ESTIMATING'
  | 'PRECONSTRUCTION'
  | 'DESIGN_COORDINATION'
  | 'PROCUREMENT'
  | 'MOBILIZATION'
  | 'ACTIVE_CONSTRUCTION'
  | 'PUNCH_LIST'
  | 'CLOSEOUT'
  | 'WARRANTY';
export type ProjectRiskLevel = 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
export type ProjectHealthBand = 'HEALTHY' | 'WATCH' | 'AT_RISK' | 'CRITICAL';
export type ProjectSortField =
  | 'name'
  | 'projectNumber'
  | 'healthScore'
  | 'contractValue'
  | 'updatedAt'
  | 'startDate'
  | 'substantialCompletion';
export type SortOrder = 'asc' | 'desc';

export interface ProjectFilters {
  search: string;
  status: ProjectStatus | '';
  phase: ProjectPhase | '';
  riskLevel: ProjectRiskLevel | '';
  sector: string;
  projectId: string;
  regionId: string;
  page: number;
  pageSize: number;
  sortBy: ProjectSortField;
  sortOrder: SortOrder;
  cohort?: string;
}

export type ProjectPortfolioFilters = Omit<
  ProjectFilters,
  'page' | 'pageSize' | 'sortBy' | 'sortOrder'
>;

export interface ProjectHealthSource {
  availability: 'recorded_model' | 'unavailable';
  score: number | null;
  band: ProjectHealthBand | null;
  snapshotId: string | null;
  calculatedAt: string | null;
}

export interface ProjectPortfolioCount {
  key: string;
  label: string;
  count: number;
  cohort: string;
}

export interface ProjectPortfolioSummary {
  generatedAt: string;
  latestProjectUpdatedAt: string | null;
  basis: string;
  matchingRegisterCount: number;
  portfolioCount: number;
  excludedCount: number;
  byStatus: ProjectPortfolioCount[];
  byRisk: ProjectPortfolioCount[];
  health: {
    basis: string;
    recordedCount: number;
    unavailableCount: number;
    byBand: ProjectPortfolioCount[];
  };
  financials: {
    availability: 'available' | 'restricted';
    basis: string;
    contractValue: string | null;
    budgetCurrent: string | null;
    actualCost: string | null;
    forecastAtCompletion: string | null;
    sectors: Array<{
      key: string;
      label: string;
      count: number;
      contractValue: string;
      cohort: string;
    }>;
  };
}

export interface ProjectFinancials {
  contractValue: string;
  budgetCurrent: string;
  actualCost: string;
  forecastAtCompletion: string;
  variance: string;
}

export interface ProjectHealthSummary {
  score: number;
  band: ProjectHealthBand;
  source?: ProjectHealthSource;
}

export interface ProjectScheduleSummary {
  startDate: string | null;
  substantialCompletion: string | null;
  taskCount: number;
  percentComplete: number | null;
}

export interface ProjectLocation {
  city: string | null;
  state: string | null;
  latitude: string | number | null;
  longitude: string | number | null;
}

export interface ProjectReference {
  id: string;
  name: string;
}

export interface RegionReference extends ProjectReference {
  code: string;
}

export interface ProjectListItem {
  id: string;
  version: number;
  projectNumber: string;
  name: string;
  clientName: string | null;
  sector: string | null;
  status: ProjectStatus;
  phase: ProjectPhase;
  riskLevel: ProjectRiskLevel;
  health: ProjectHealthSummary;
  financials: ProjectFinancials | null;
  schedule: ProjectScheduleSummary;
  location: ProjectLocation;
  organization: ProjectReference | null;
  region: RegionReference | null;
  /** Kept out of portfolio totals, maps and KPIs by an operator (e.g. a Procore template). */
  portfolio: { excluded: boolean; reason: string | null };
  createdAt: string;
  updatedAt: string;
}

export interface ProjectHealthCurrent extends ProjectHealthSummary {
  budgetScore: number | null;
  riskScore: number;
  scheduleScore: number;
  deliveryScore: number;
  reasons: string[];
  calculatedAt: string;
}

export interface ProjectHealthHistoryItem {
  id: string;
  score: number;
  budgetScore: number | null;
  riskScore: number;
  scheduleScore: number;
  deliveryScore: number;
  calculatedAt: string;
}

export interface ProjectTask {
  id: string;
  name: string;
  status: string;
  plannedStart: string | null;
  plannedFinish: string | null;
  percentComplete: number;
  isCritical: boolean;
}

export interface ProjectAlert {
  id: string;
  title: string;
  message: string;
  category: string;
  severity: ProjectRiskLevel;
  status: string;
  createdAt: string;
}

export interface ProjectRecommendation {
  id: string;
  title: string;
  summary: string;
  severity: ProjectRiskLevel;
  status: string;
  confidenceScore: number | null;
  recommendedAction: string;
  createdAt: string;
}

export interface ProjectPermissions {
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canExecute: boolean;
  canExport: boolean;
  canReadFinance: boolean;
}

export interface ProjectDetail extends Omit<ProjectListItem, 'health' | 'schedule'> {
  health: ProjectHealthSummary & {
    current: ProjectHealthCurrent;
    history: ProjectHealthHistoryItem[];
  };
  schedule: ProjectScheduleSummary & { tasks: ProjectTask[] };
  alerts: ProjectAlert[];
  recommendations: ProjectRecommendation[];
  permissions: ProjectPermissions;
}

export interface ProjectTimelineItem {
  id: string;
  type: 'STATUS' | 'HEALTH' | 'AUDIT';
  action: string;
  fromStatus: ProjectStatus | null;
  toStatus: ProjectStatus | null;
  score: number | null;
  actor: { id: string; name: string } | null;
  reason: string | null;
  timestamp: string;
}

export interface ProjectTimeline {
  items: ProjectTimelineItem[];
}

export interface ProjectPage {
  items: ProjectListItem[];
  meta: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface ProjectInput {
  name: string;
  projectNumber: string;
  phase: ProjectPhase;
  organizationId?: string | null;
  regionId?: string | null;
  clientName?: string | null;
  sector?: string | null;
  riskLevel?: ProjectRiskLevel;
  startDate?: string | null;
  substantialCompletion?: string | null;
  city?: string | null;
  state?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export type UpdateProjectInput = Partial<ProjectInput> & { version: number };

export type ProjectActionInput =
  | { action: 'RECALCULATE_HEALTH'; version: number; reason?: string }
  | { action: 'CHANGE_STATUS'; version: number; status: ProjectStatus; reason?: string }
  | { action: 'RESTORE'; version: number; reason?: string }
  | { action: 'EXCLUDE_FROM_PORTFOLIO'; version: number; reason: string }
  | { action: 'INCLUDE_IN_PORTFOLIO'; version: number; reason?: string };

export interface ArchiveProjectResult {
  id: string;
  version: number;
  archivedAt: string;
}

export interface ProjectIdentity {
  tenantId: string;
  userId: string;
  permissions?: string;
  sessionOrigin?: string | null;
}

export const PROJECT_STATUSES: ProjectStatus[] = [
  'PLANNING',
  'ACTIVE',
  'ON_HOLD',
  'COMPLETED',
  'CANCELLED',
];

export const PROJECT_STATUS_TRANSITIONS: Record<ProjectStatus, readonly ProjectStatus[]> = {
  PLANNING: ['ACTIVE', 'ON_HOLD', 'CANCELLED'],
  ACTIVE: ['ON_HOLD', 'COMPLETED', 'CANCELLED'],
  ON_HOLD: ['ACTIVE', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

export function hasProjectStatusTransition(status: ProjectStatus): boolean {
  return PROJECT_STATUS_TRANSITIONS[status].length > 0;
}

export const PROJECT_PHASES: ProjectPhase[] = [
  'PURSUIT',
  'ESTIMATING',
  'PRECONSTRUCTION',
  'DESIGN_COORDINATION',
  'PROCUREMENT',
  'MOBILIZATION',
  'ACTIVE_CONSTRUCTION',
  'PUNCH_LIST',
  'CLOSEOUT',
  'WARRANTY',
];

export const PROJECT_RISK_LEVELS: ProjectRiskLevel[] = ['LOW', 'MODERATE', 'HIGH', 'CRITICAL'];
